import { useLocation } from '@solidjs/router';
import { createEffect, Match, Switch, useContext } from 'solid-js';
import { Meta, Title } from '@solidjs/meta';
import { AppContext } from '../../app-context-provider/app-context-provider';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { ErrorCatcher } from '../../tools/error-catcher';
import { GET_PAGE_QUERY } from '../page/page';
import { SeoSettings } from '../seo-settings/seo-settings';
import { Heading } from '../../ui-components/heading/heading';
import { Button } from '../../ui-components/button/button';
import { Section } from '../../grid-system/section/section';
import { PageCacheType } from '../../types/shared';

export const NotFound = () => {
    const location = useLocation();

    const { 
        createCachedResource,
        page404,
        localize,
        siteInfo,
        headerSettings,
        appStore,
        setCacheType,
    } = useContext(AppContext);

    const [ page404Content ] = createCachedResource(GET_PAGE_QUERY, () => ({ key: page404.url }), true);

    const showHcpHeader = () => headerSettings.activateHcpHeader && isHcpPage();

    const isHcpPage = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }
        
        return Boolean(page404Content()?.page?.content?.isHealthcareProfessional);
    };

    const setHcp = () => {
        const isHcp = checkHcp();
        appStore.setHcp(isHcp);
        return null;
    };

    setHcp();

    createEffect(() => {
        setHcp();
    });

    const pageTitle = () => {
        return siteInfo.siteType === 'atos-care' ? 'Atos Care' : 'Atos Medical';
    };

    return (
        <ErrorCatcher componentName='Not Found'>
            <Meta name="robots" content="none" />
            <div class={`${showHcpHeader() ? 'mt-[6rem] desktop:mt-[8rem]' : ''}`}>
                <Switch>
                    <Match when={!page404Content() || !page404Content()?.page}>
                        <Section widthType='bgFull'>
                            <Title>{`${localize('page-not-found', 'Page not found')} - ${pageTitle()}`}</Title>
                            <div class="flex flex-col items-center gap-8 m-20">
                                <Heading tag='h1' variant='xlarge'>{localize('page-not-found', 'Page not found')}</Heading>
                                <Button label={localize('back-to-home', 'Back to home')} isLink url='/' />
                            </div>
                        </Section>
                    </Match>

                    <Match when={page404?.url && page404.url !== ''}>
                        <SeoSettings
                            seo={page404Content()?.page?.content?.seo}
                            title={page404Content()?.page?.content?.post_title}
                        />
                        <DynamicModuleGenerator
                            content={page404Content()?.page?.content.blocks}
                            permalink={page404Content()?.page?.content.permalink}
                            pageTitle={page404Content()?.page?.content.post_title}
                        />
                    </Match>
                </Switch>
            </div>
        </ErrorCatcher>
    );
};
